import { ExerciseStateMachine } from './ExerciseStateMachine';

export const SeatedMarchesState = Object.freeze({
  DOWN: 'DOWN',               // Feet on floor (starting position)
  RAISING: 'RAISING',         // In the process of raising knee
  RAISED: 'RAISED',           // Knee fully raised
  LOWERING: 'LOWERING'        // In the process of lowering knee
});

const MIN_FRAMES_IN_POSITION = 2;      // Frames required in a position
const MIN_FRAMES_TO_ARM = 4;           // Frames above threshold to arm detection
const MIN_FRAMES_TO_DISARM = 4;        // Frames below threshold to disarm detection
const INTERNAL_COOLDOWN_FRAMES = 5;    // Frame-based cooldown

const BASE_MIN_RATIO = 0.65;           // Base minimum ratio threshold
const BASE_TARGET_RATIO = 0.85;        // Base target ratio threshold
const LOW_VISIBILITY_THRESHOLD = 0.4;  // Consider landmark visible above this threshold
const CAMERA_POSITION_THRESHOLD = 0.15; // Camera position warning threshold (15%)
const ALERT_CAMERA_FRAMES = 5;         // Frames of bad camera position before alert

const ALPHA = 0.4;                     // Position filter weight
const BETA = 0.2;                      // Velocity filter weight

export class SeatedMarchesFSM extends ExerciseStateMachine {
  constructor() {
    const config = {
      initialState: SeatedMarchesState.DOWN,
      initialStateData: {
        activeLeg: 'right',            // Default to right leg
        frameCount: 0,                 // Total frame counter
        framesPerSecond: 30,           // Assumed FPS, will be calculated
        lastFrameTime: 0,              // Time of last frame
        
        kneeHipAnkleRatio: 0,          // Current knee-hip ratio to ankle-hip distance
        ratioFilter: {
          x: 0,                        // Smoothed ratio
          v: 0,                        // Velocity
        },
        
        isCalibrated: false,
        calibrationFrames: 0,
        restingRatio: 0,               // Ratio at rest
        calibratedTargetRatio: 0,      // Calibrated target ratio
        calibratedMinRatio: 0,         // Calibrated minimum ratio for detection
        
        consecutiveRaisedFrames: 0,    // Frames above threshold
        consecutiveLoweredFrames: 0,   // Frames below threshold
        stableDownFrames: 0,           // Frames in stable down position
        stableRaisedFrames: 0,         // Frames in stable raised position
        internalCooldown: 0,           // Cooldown counter (frames)
        badCameraPositionFrames: 0,    // Frames with poor camera position
        
        heightScore: 0,                // Range of motion score
        controlScore: 100,             // Control score
        balanceScore: 100,             // Balance score
        
        previousLandmarks: null,       // Previous frame landmarks
        isRaising: false,              // Currently raising
        isLowering: false,             // Currently lowering
        velocityHistory: [],           // For calculating stability
        
        lastStateChangeFrame: 0,       // Frame when state last changed
        lastDownFrame: 0,              // Frame when last in DOWN state
        lastRaisedFrame: 0,            // Frame when last in RAISED state
        maxRatio: 0,                   // Maximum ratio achieved
      },
      initialFormScores: {
        overall: 100,
        rangeOfMotion: 0,
        control: 100,
        balance: 100
      },
      transitions: {
        [SeatedMarchesState.DOWN]: [
          {
            targetState: SeatedMarchesState.RAISING,
            condition: (metrics, stateData) => {
              return (stateData.internalCooldown <= 0) && 
                     metrics.isRaising &&
                     (metrics.filteredRatio > stateData.calibratedMinRatio) &&
                     (stateData.consecutiveRaisedFrames >= MIN_FRAMES_TO_ARM) &&
                     (stateData.frameCount - stateData.lastRaisedFrame > MIN_FRAMES_IN_POSITION);
            },
            action: (fsm, metrics) => {
              fsm.stateData.lastDownFrame = fsm.stateData.frameCount;
              fsm.stateData.stableDownFrames = 0;
              fsm.stateData.maxRatio = metrics.filteredRatio;
            }
          }
        ],
        
        [SeatedMarchesState.RAISING]: [
          {
            targetState: SeatedMarchesState.RAISED,
            condition: (metrics, stateData) => {
              return (metrics.filteredRatio > stateData.calibratedTargetRatio * 0.97) &&
                     (stateData.frameCount - stateData.lastDownFrame > MIN_FRAMES_IN_POSITION);
            },
            action: (fsm, metrics) => {
              fsm.stateData.lastRaisedFrame = fsm.stateData.frameCount;
              fsm.stateData.maxRatio = Math.max(fsm.stateData.maxRatio, metrics.filteredRatio);
              
              fsm.stateData.internalCooldown = INTERNAL_COOLDOWN_FRAMES;
              
              if (!fsm.stateData.isCalibrated && fsm.stateData.calibrationFrames < 40) {
                fsm.updateCalibration(fsm.stateData.maxRatio);
              }
            },
            completesRepetition: true,
            calculateRepScore: (metrics, stateData) => {
              return Math.round(
                (metrics.heightScore * 0.5) + 
                (metrics.balanceScore * 0.3) + 
                (metrics.controlScore * 0.2)
              );
            }
          },
          {
            targetState: SeatedMarchesState.DOWN,
            condition: (metrics, stateData) => {
              return metrics.isLowering && 
                     (metrics.filteredRatio < stateData.calibratedMinRatio) &&
                     (stateData.consecutiveLoweredFrames >= MIN_FRAMES_TO_DISARM);
            }
          }
        ],
        
        [SeatedMarchesState.RAISED]: [
          {
            targetState: SeatedMarchesState.LOWERING,
            condition: (metrics, stateData) => {
              return metrics.isLowering && 
                     (metrics.filteredRatio < metrics.maxObservedRatio - 0.05) &&
                     (stateData.frameCount - stateData.lastRaisedFrame > MIN_FRAMES_IN_POSITION);
            },
            action: (fsm) => {
              fsm.stateData.stableRaisedFrames = 0;
            }
          },
          {
            targetState: SeatedMarchesState.DOWN,
            condition: (metrics, stateData) => {
              return metrics.filteredRatio < stateData.calibratedMinRatio;
            }
          }
        ],
        
        [SeatedMarchesState.LOWERING]: [
          {
            targetState: SeatedMarchesState.DOWN,
            condition: (metrics, stateData) => {
              return (metrics.filteredRatio < stateData.calibratedMinRatio) &&
                     (stateData.consecutiveLoweredFrames >= MIN_FRAMES_TO_DISARM);
            },
            action: (fsm) => {
              fsm.stateData.lastDownFrame = fsm.stateData.frameCount;
              
              const newActiveLeg = fsm.stateData.activeLeg === 'right' ? 'left' : 'right';
              fsm.stateData.activeLeg = newActiveLeg;
              
              fsm.stateData.internalCooldown = 0;
              
              fsm.stateData.consecutiveRaisedFrames = 0;
              fsm.stateData.consecutiveLoweredFrames = 0;
              fsm.stateData.velocityHistory = [];
              fsm.stateData.maxRatio = 0;
              
              fsm.stateData.ratioFilter = { x: 0, v: 0 };
              
              fsm.stateData.calibratedMinRatio = 0;
              fsm.stateData.calibratedTargetRatio = 0;
              fsm.stateData.restingRatio = 0;
              fsm.stateData.isCalibrated = false;
              fsm.stateData.calibrationFrames = 0;
            }
          },
          {
            targetState: SeatedMarchesState.RAISING,
            condition: (metrics, stateData) => {
              return (stateData.internalCooldown <= 0) && 
                     metrics.isRaising && 
                     (metrics.filteredRatio > stateData.calibratedMinRatio) &&
                     (stateData.consecutiveRaisedFrames >= MIN_FRAMES_TO_ARM);
            }
          }
        ]
      }
    };
    
    super(config);
    
    this.constructor.initialConfig = {
      initialState: config.initialState,
      initialStateData: { ...config.initialStateData },
      initialFormScores: { ...config.initialFormScores }
    };
  }
  
  setActiveLeg(leg) {
    if (this.stateData.activeLeg !== leg) {
      this.stateData.activeLeg = leg;
      
      this.stateData.kneeHipAnkleRatio = 0;
      this.stateData.ratioFilter = { x: 0, v: 0 };
      this.stateData.consecutiveRaisedFrames = 0;
      this.stateData.consecutiveLoweredFrames = 0;
      this.stateData.velocityHistory = [];
      this.stateData.isCalibrated = false;
      this.stateData.calibrationFrames = 0;
      this.stateData.maxRatio = 0;
      this.stateData.internalCooldown = 0;
      
      if (this.currentState !== SeatedMarchesState.DOWN) {
        this.transitionTo(SeatedMarchesState.DOWN);
      }
    }
  }
  
  updateCalibration(maxRatio) {
    this.stateData.calibrationFrames++;
    
    if (this.stateData.calibrationFrames >= 2) {
      if (this.stateData.restingRatio === 0) {
        this.stateData.restingRatio = this.stateData.ratioFilter.x;
      }
      
      const headroom = 0.15;                     // 15% above rest is plenty
      const peakMargin = 0.90;                   // use 90%, not 95%, of raw peak
      
      const newTargetRatio = Math.max(
        this.stateData.restingRatio + headroom,
        maxRatio * peakMargin
      );
      
      this.stateData.calibratedTargetRatio = newTargetRatio;
      
      this.stateData.calibratedMinRatio = 
        this.stateData.restingRatio + 
        (newTargetRatio - this.stateData.restingRatio) * BASE_MIN_RATIO;
      
      if (this.stateData.calibrationFrames >= 10) {
        this.stateData.isCalibrated = true;
      }
    }
  }
  
  applyAlphaBetaFilter(filter, measurement) {
    const predictedPosition = filter.x + filter.v;
    
    const residual = measurement - predictedPosition;
    const correctedPosition = predictedPosition + ALPHA * residual;
    const correctedVelocity = filter.v + BETA * residual;
    
    return {
      x: correctedPosition,
      v: correctedVelocity
    };
  }
  
  // Compute metrics from pose landmarks
  computeMetrics(landmarks, worldLandmarks, timestamp) {
    if (!worldLandmarks) {
      return {
        isRaising: false,
        isLowering: false,
        filteredRatio: 0,
        landmarksValid: false
      };
    }
    
    const now = timestamp;
    if (this.stateData.lastFrameTime > 0) {
      const frameTime = now - this.stateData.lastFrameTime;
      if (frameTime > 0) {
        this.stateData.framesPerSecond = 
          0.9 * this.stateData.framesPerSecond + 
          0.1 * (1000 / frameTime);
      }
    }
    this.stateData.lastFrameTime = now;
    this.stateData.frameCount++;
    
    if (this.stateData.internalCooldown > 0) {
      this.stateData.internalCooldown--;
    }
    
    const activeSide = this.stateData.activeLeg || 'right';
    const hipIdx = activeSide === 'right' ? 24 : 23;
    const kneeIdx = activeSide === 'right' ? 26 : 25;
    const ankleIdx = activeSide === 'right' ? 28 : 27;
    
    if (!worldLandmarks[hipIdx] || !worldLandmarks[kneeIdx]) {
      return {
        isRaising: false,
        isLowering: false,
        filteredRatio: this.stateData.ratioFilter.x,
        landmarksValid: false
      };
    }
    
    let hipVisible = true;
    let kneeVisible = true;
    let ankleVisible = true;
    
    if (landmarks && landmarks[0]) {
      hipVisible = !landmarks[0][hipIdx] || landmarks[0][hipIdx].visibility > LOW_VISIBILITY_THRESHOLD;
      kneeVisible = !landmarks[0][kneeIdx] || landmarks[0][kneeIdx].visibility > LOW_VISIBILITY_THRESHOLD;
      ankleVisible = !landmarks[0][ankleIdx] || landmarks[0][ankleIdx].visibility > LOW_VISIBILITY_THRESHOLD;
    }
    
    const hip = worldLandmarks[hipIdx];
    const knee = worldLandmarks[kneeIdx];
    const ankle = worldLandmarks[ankleIdx] || { x: knee.x, y: knee.y + 0.15, z: knee.z }; // Synthesize ankle if missing
    
    let cameraPositionOk = true;
    let cameraPositionRatio = 0;
    
    if (worldLandmarks[11] && worldLandmarks[12] && worldLandmarks[23] && worldLandmarks[24]) {
      const leftShoulder = worldLandmarks[11];
      const rightShoulder = worldLandmarks[12];
      const leftHip = worldLandmarks[23];
      const rightHip = worldLandmarks[24];
      
      const shoulderHipVerticalRatio = Math.abs((leftShoulder.y + rightShoulder.y)/2 - (leftHip.y + rightHip.y)/2);
      const shoulderHipHorizontalDist = Math.abs((leftShoulder.x + rightShoulder.x)/2 - (leftHip.x + rightHip.x)/2);
      
      if (shoulderHipHorizontalDist > 0.001) {
        cameraPositionRatio = shoulderHipVerticalRatio / shoulderHipHorizontalDist;
        
        if (Math.abs(cameraPositionRatio - 0.5) > CAMERA_POSITION_THRESHOLD) {
          this.stateData.badCameraPositionFrames++;
          if (this.stateData.badCameraPositionFrames > ALERT_CAMERA_FRAMES) {
            cameraPositionOk = false;
          }
        } else {
          this.stateData.badCameraPositionFrames = 0;
        }
      }
    }
    
    
    const hipKneeDistance = this.calculateDistance3D(hip, knee);
    const kneeAnkleDistance = this.calculateDistance3D(knee, ankle);
    const hipAnkleDistance = this.calculateDistance3D(hip, ankle);
    
    let kneeHipAnkleRatio = 0;
    
    if (hipAnkleDistance > 0.001) {
      kneeHipAnkleRatio = hipKneeDistance / hipAnkleDistance;
    }
    
    if (this.stateData.ratioFilter.x === 0) {
      this.stateData.ratioFilter = {
        x: kneeHipAnkleRatio,
        v: 0
      };
    } else {
      this.stateData.ratioFilter = this.applyAlphaBetaFilter(
        this.stateData.ratioFilter,
        kneeHipAnkleRatio
      );
    }
    
    const filteredRatio = this.stateData.ratioFilter.x;
    const velocity = this.stateData.ratioFilter.v;
    
    const isRaising = velocity > 0.002;
    const isLowering = velocity < -0.002;
    
    if (!this.stateData.isCalibrated && this.stateData.calibratedMinRatio === 0) {
      this.stateData.calibratedMinRatio = filteredRatio + 0.05;
      this.stateData.calibratedTargetRatio = filteredRatio + 0.15;
      this.stateData.restingRatio = filteredRatio;
    }
    
    if (filteredRatio > this.stateData.calibratedMinRatio) {
      this.stateData.consecutiveRaisedFrames++;
      this.stateData.consecutiveLoweredFrames = 0;
    } else {
      this.stateData.consecutiveRaisedFrames = 0;
      this.stateData.consecutiveLoweredFrames++;
    }
    
        
    const heightPercent = Math.min(1, (filteredRatio - this.stateData.restingRatio) / 
                                     (this.stateData.calibratedTargetRatio - this.stateData.restingRatio));
    const heightScore = Math.min(100, heightPercent * 100);
    
    this.stateData.velocityHistory.push(velocity);
    if (this.stateData.velocityHistory.length > 5) {
      this.stateData.velocityHistory.shift();
    }
    
    let controlScore = 100;
    if (this.stateData.velocityHistory.length >= 3) {
      const avgVelocity = this.stateData.velocityHistory.reduce((sum, v) => sum + v, 0) / 
                         this.stateData.velocityHistory.length;
      
      const velocityVariance = this.stateData.velocityHistory.reduce(
        (sum, v) => sum + Math.pow(v - avgVelocity, 2), 0) / 
        this.stateData.velocityHistory.length;
      
      controlScore = Math.max(0, 100 - (velocityVariance * 10000));
    }
    
    const balanceScore = hipVisible && kneeVisible ? 100 : 80;
    
    const maxObservedRatio = Math.max(this.stateData.maxRatio || 0, filteredRatio);
    
    return {
      kneeHipAnkleRatio,
      filteredRatio,
      velocity,
      isRaising,
      isLowering,
      heightScore,
      controlScore,
      balanceScore,
      hipVisible,
      kneeVisible,
      ankleVisible,
      landmarksValid: true,
      cameraPositionOk,
      cameraPositionRatio, 
      maxObservedRatio
    };
  }
  
  calculateDistance3D(point1, point2) {
    const dx = point2.x - point1.x;
    const dy = point2.y - point1.y;
    const dz = point2.z - point1.z;
    return Math.sqrt(dx*dx + dy*dy + dz*dz);
  }
  
  updateFormScores(metrics, transitioned) {
    if (!metrics || !metrics.landmarksValid) return;
    
    const heightWeight = 0.5;
    const balanceWeight = 0.3;
    const controlWeight = 0.2;
    
    const overallScore = Math.round(
      (metrics.heightScore * heightWeight) +
      (metrics.balanceScore * balanceWeight) +
      (metrics.controlScore * controlWeight)
    );
    
    const alpha = 0.3; 
    this.formScores.overall = Math.round(
      (1 - alpha) * this.formScores.overall + alpha * overallScore
    );
    
    this.formScores.rangeOfMotion = metrics.heightScore;
    this.formScores.balance = metrics.balanceScore;
    this.formScores.control = metrics.controlScore;
  }
  
  generateFormFeedback(metrics, currentState) {
    if (!metrics || !metrics.landmarksValid) return null;
    
    if (!metrics.cameraPositionOk) {
      return "Please adjust camera position to better view your seated posture";
    }
    
    if (Math.random() > 0.05) return this.formFeedback;
    
    if (metrics.heightScore < 60 && currentState === SeatedMarchesState.RAISED) {
      return "Try to raise your knee higher for better range of motion";
    }
    
    if (metrics.controlScore < 70) {
      return "Move more smoothly for better control";
    }
    
    return null;
  }
  
  process(results, timestamp) {
    const pose = results.landmarks && results.landmarks.length > 0 ? results.landmarks[0] : null;
    const worldPose = results.worldLandmarks && results.worldLandmarks.length > 0 ? results.worldLandmarks[0] : null;
    
    const metrics = this.computeMetrics(results.landmarks, worldPose, timestamp);
    
    if (this.stateData.lastStateChangeFrame > 0 && 
        this.stateData.frameCount - this.stateData.lastStateChangeFrame < MIN_FRAMES_IN_POSITION) {
      metrics.filteredRatio = metrics.filteredRatio * 0.9;
    }
    
    const previousState = this.currentState;
    const result = super.process(results, timestamp);
    
    if (previousState !== this.currentState) {
      this.stateData.lastStateChangeFrame = this.stateData.frameCount;
    }
    
    if (this.currentState === SeatedMarchesState.DOWN) {
      this.stateData.stableDownFrames++;
    } else {
      this.stateData.stableDownFrames = 0;
    }
    
    if (this.currentState === SeatedMarchesState.RAISED) {
      this.stateData.stableRaisedFrames++;
    } else {
      this.stateData.stableRaisedFrames = 0;
    }
    
    return result;
  }
} 